<template>
    <BaseDialog v-bind="$attrs" v-model:open="open" :title="t('components.workIq.settings.title')">
        <p class="body-small text-ink-muted">{{ t("components.workIq.settings.subtitle") }}</p>

        <section class="flex flex-col gap-2.5">
            <h3 class="body-small-semibold text-ink">{{ t("components.workIq.settings.microsoft365") }}</h3>
            <div class="flex flex-row justify-between items-center gap-3">
                <div class="flex flex-row gap-2.5 items-center min-w-0">
                    <i class="bi bi-microsoft text-ink-muted shrink-0" aria-hidden="true"></i>
                    <span class="body-small text-ink-muted truncate">
                        <i class="bi bi-check-circle-fill text-status-success mr-1" aria-hidden="true"></i>
                        {{ t("components.workIq.settings.connectedAs", { email: userDetails?.email }) }}
                    </span>
                </div>
                <BaseButton v-if="!confirming" theme="tertiary" size="sm" class="shrink-0" @click="confirming = true">{{ t("components.workIq.settings.disconnect") }}</BaseButton>
            </div>
            <InlineConfirm
                v-model:open="confirming"
                :prompt="t('components.workIq.settings.disconnectPrompt')"
                :confirm-label="t('components.workIq.settings.confirmDisconnect')"
                :is-confirming="isDisconnecting"
                @confirm="removeWorkIqIntegration" />
        </section>

        <section class="flex flex-col gap-2.5">
            <div class="flex flex-row justify-between items-baseline gap-3">
                <h3 class="body-small-semibold text-ink">{{ t("components.workIq.settings.whatNadiaCanUse") }}</h3>
                <BaseButton type="button" theme="tertiary" size="sm" :disabled="isSavingSources" @click="setAllSources(!allSourcesEnabled)">
                    {{ allSourcesEnabled ? t("components.workIq.settings.disableAll") : t("components.workIq.settings.enableAll") }}
                </BaseButton>
            </div>
            <ul class="flex flex-col gap-2">
                <li v-for="source in sourceStates" :key="source.key">
                    <BaseCard theme="cream" padding="none" radius="md">
                        <!-- Whole row is the label, so the switch is nested rather than a sibling
                                 and the description is part of the clickable target. -->
                        <label :for="`workiq-source-${source.source}`" class="flex flex-row items-center gap-4 px-4 py-3 cursor-pointer">
                            <i :class="['bi', iconFor(source.source), 'text-ink-muted text-lg shrink-0']" aria-hidden="true"></i>
                            <span class="flex flex-col min-w-0 grow">
                                <span class="body-small-semibold text-ink">{{ source.label }}</span>
                                <span class="scaffold-details-small text-ink-muted">{{ source.description }}</span>
                            </span>
                            <BaseSwitch
                                :id="`workiq-source-${source.source}`"
                                size="md"
                                :model-value="source.enabled"
                                :disabled="isSavingSources"
                                class="shrink-0"
                                @update:model-value="(value: boolean) => setSource(source, value)" />
                        </label>
                    </BaseCard>
                </li>
            </ul>
            <p v-if="noSourcesEnabled" class="scaffold-details-small text-ink-muted">{{ t("components.workIq.settings.noSources") }}</p>
            <p v-if="sourcesError" role="alert" class="scaffold-details-small text-status-error">{{ t("components.workIq.settings.saveFailed") }}</p>
        </section>

        <BaseCard theme="cream-muted" padding="sm" radius="md" class="flex flex-col gap-1">
            <label for="workiq-auto-context" class="flex flex-row items-center justify-between gap-3 cursor-pointer">
                <span class="body-small-semibold text-ink">{{ t("components.workIq.settings.autoContext") }}</span>
                <BaseSwitch
                    id="workiq-auto-context"
                    size="md"
                    :model-value="autoContextEnabled"
                    :disabled="isSavingAutoContext"
                    class="shrink-0"
                    @update:model-value="setAutoContext" />
            </label>
            <p class="scaffold-details-small text-ink-muted">{{ autoContextDescription }}</p>
            <p v-if="autoContextError" role="alert" class="scaffold-details-small text-status-error">{{ t("components.workIq.settings.saveFailed") }}</p>
        </BaseCard>
    </BaseDialog>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import InlineConfirm from "~vue/components/InlineConfirm.vue";
import { logUserInteraction } from "~vue/utils/logUtils";
import type { Ref } from "vue";
import { computed, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineOptions({ inheritAttrs: false });

type WorkIqSource = {
    source: string;
    key: string;
    label: string;
    description: string;
    enabled: boolean;
};

const props = defineProps<{
    disconnectUrl: string;
    autoContext?: boolean;
    sources?: WorkIqSource[];
}>();

const open = defineModel<boolean>("open");

const userDetails = inject<Ref<UserDetails>>("user_details");
const confirming = ref(false);

// Icons live here rather than with the rest of the source catalogue in workiq/sources.py: they are
// presentation with no bearing on what we promise to read, unlike the label and description. Keyed
// by source so a new source added server-side renders with the fallback instead of a blank column.
const SOURCE_ICONS: Record<string, string> = {
    transcripts: "bi-mic",
    teams: "bi-chat-dots",
    email: "bi-envelope",
    calendar: "bi-calendar-event",
    documents: "bi-file-earmark-text",
    people: "bi-people",
};
const iconFor = (source: string) => SOURCE_ICONS[source] ?? "bi-microsoft";

const autoContextEnabled = ref<boolean>(props.autoContext ?? false);
const autoContextPayload = ref<{ key: string; value: boolean } | null>(null);
const { execute: executeAutoContextUpdate, error: autoContextError } = useFetch("/api/accounts/user/settings", { immediate: false }).post(autoContextPayload).json();

const sourceStates = ref<WorkIqSource[]>((props.sources ?? []).map((source) => ({ ...source })));
const sourcePayload = ref<{ key: string; value: boolean } | null>(null);
const { execute: executeSourceUpdate, error: sourcesError } = useFetch("/api/accounts/user/settings", { immediate: false }).post(sourcePayload).json();

const allSourcesEnabled = computed(() => sourceStates.value.length > 0 && sourceStates.value.every((source) => source.enabled));
const noSourcesEnabled = computed(() => sourceStates.value.length > 0 && sourceStates.value.every((source) => !source.enabled));

// One request per source, because the settings endpoint takes a single key/value pair. Sent in
// series rather than in parallel: they share `sourcePayload`, and the whole point of "Enable all"
// is that a partial failure leaves the UI showing what actually saved.
const persistSource = async (source: WorkIqSource, value: boolean) => {
    const target = sourceStates.value.find((candidate) => candidate.key === source.key);
    if (!target || target.enabled === value) {
        return true;
    }
    const previous = target.enabled;
    target.enabled = value;
    sourcePayload.value = { key: source.key, value };
    await executeSourceUpdate();
    if (sourcesError.value) {
        target.enabled = previous;
        return false;
    }
    logUserInteraction(`workiq_source_${source.source}_${value ? "enabled" : "disabled"}`);
    return true;
};

// All six switches and the bulk action share `sourcePayload` and one `useFetch` instance, so two
// overlapping saves can interleave: the second mutates the payload the first has not sent yet, and
// whichever resolves last wins the optimistic state. For a privacy control that means the UI can
// show a source as off while the server has it on, so only one save runs at a time and the controls
// are disabled while it does. Saves are sub-second, and the bulk action is six of them in series.
const isSavingSources = ref(false);

const runSourceSave = async (save: () => Promise<void>) => {
    if (isSavingSources.value) {
        return;
    }
    isSavingSources.value = true;
    try {
        await save();
    } finally {
        isSavingSources.value = false;
    }
};

const setSource = (source: WorkIqSource, value: boolean) =>
    runSourceSave(async () => {
        await persistSource(source, value);
    });

const setAllSources = (value: boolean) =>
    runSourceSave(async () => {
        for (const source of [...sourceStates.value]) {
            if (!(await persistSource(source, value))) {
                return;
            }
        }
        logUserInteraction(value ? "workiq_all_sources_enabled" : "workiq_all_sources_disabled");
    });

// Points at the sources toggles rather than listing them, so this line stays short and cannot
// promise a source the user has switched off.
const autoContextDescription = computed(() => (noSourcesEnabled.value ? t("components.workIq.settings.autoContextOff") : t("components.workIq.settings.autoContextOn")));

// Same reasoning as `isSavingSources`, for the one control that shares `autoContextPayload`.
const isSavingAutoContext = ref(false);

const setAutoContext = async (value: boolean) => {
    if (isSavingAutoContext.value) {
        return;
    }
    isSavingAutoContext.value = true;
    try {
        await persistAutoContext(value);
    } finally {
        isSavingAutoContext.value = false;
    }
};

const persistAutoContext = async (value: boolean) => {
    const previous = autoContextEnabled.value;
    autoContextEnabled.value = value;
    autoContextPayload.value = { key: "workiq_auto_context", value };
    await executeAutoContextUpdate();
    if (autoContextError.value) {
        autoContextEnabled.value = previous;
        return;
    }
    logUserInteraction(value ? "workiq_auto_context_enabled" : "workiq_auto_context_disabled");
};

// Component stays mounted across open/close, so reset the confirm prompt when the dialog closes.
watch(open, (isOpen) => {
    if (!isOpen) {
        confirming.value = false;
    }
});

// Same reason: a server round trip (e.g. the reload after disconnect) refreshes the props
// without remounting, so the local state has to follow them.
watch(
    () => props.autoContext,
    (value) => {
        autoContextEnabled.value = value ?? false;
    },
);

watch(
    () => props.sources,
    (value) => {
        sourceStates.value = (value ?? []).map((source) => ({ ...source }));
    },
);

const { execute: executeDisconnect, isFetching: isDisconnecting, onFetchResponse } = useFetch(() => props.disconnectUrl, { immediate: false }).post();

const removeWorkIqIntegration = async () => {
    await executeDisconnect();
    logUserInteraction("workiq_integration_disconnected");
};

onFetchResponse(() => {
    confirming.value = false;
    router.reload();
});
</script>

<style scoped></style>
